// A Sources item id is a pick: 'none' | <deviceId> | 'screen' | 'file:<libraryId>' | 'shader:<name>'; 'video_file' is the open-a-file item. A pick sets a shape's fill to a source id, where any device is 'camera': this machine's camera.
import { createSelector } from '@reduxjs/toolkit'
import { isVirtualCamera } from '~/lib/cameras'
import { boxData } from '~/lib/migrations/steps/rectToPath'
import { HEIGHT, SHOW_DEV_UI, WIDTH } from '~/lib/constants'
import SHADERS, { isShaderSource, isShipped, SHADER_NAMES } from '~/lib/shaders'
import { CAMERA_SOURCE, fillUsersOf, isSourceFill, sourceFill } from '~/lib/sourceFill'
import { allowScreenPrompt, stageIncoming, syncFillStage } from '~/lib/sources/fillStage'
import { fillPictures } from '~/lib/sources/fillSources'
import { fingerprintOf } from '~/lib/sources/files'
import { fileSourceId, isFileSource, libraryIdOf } from '~/lib/sources/ids'
import { dropCues } from '~/lib/sources/playhead'
import { provide } from '~/lib/sources/videoPool'
import transitionManager from '~/lib/transitionManager'
import { openLibraryFile, pickVideoFile } from '~/lib/videoFilePicker'
import { newObjectId } from '~/lib/objectIds'
import { rememberCamera, setCameraDevice } from '~/store/camera'
import liveReducer, { applySourceFill, setSourceValues } from '~/store/live'
import { selectLiveProps } from '~/store/playback/selectors'
import { setObjectSelection } from '~/store/selection'
import { selectSettings } from '~/store/settings/selectors'
import { selectTexturesReady } from '~/store/ui/selectors'

export const SOURCE_TYPE_ORDER = ['none', 'camera', 'screen', 'video_file', 'shader']

const FIXED = ['none', 'screen', 'video_file']

/** A library entry's browser item: `state` 'ready' opens now, 'locked' asks for permission on click. A folder file is named by its path in the folder, so two `clip.mp4` in different subfolders tell apart. */
const fileItem = e => ({
	id: fileSourceId(e.id),
	name: e.storage === 'folder' ? e.ref.path.join('/') : e.name,
	type: 'video_file',
	thumbnailUrl: e.thumbUrl ?? undefined,
	storage: e.storage,
	folder: e.storage === 'folder' ? e.ref.folder : undefined,
	state: e.openable ? 'ready' : 'locked',
})

const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })

/** Flat, type-grouped item list for the sources browser; within a type, items sort by name. `cameras` is the store's `available` list; only its real devices are used — the fixed kinds are always present regardless of enumeration state. `files` are library entries (`fileEntries()`), each a `file:<id>` item after the open-a-file one; `missing` (`{ id, name }`) are file sources a preset names that resolve to no local file, sorted among them. */
export const buildSourceItems = (cameras = [], files = [], missing = []) => {
	const devices = cameras
		.filter(c => c.deviceId && !FIXED.includes(c.deviceId))
		.map((c, i) => ({ id: c.deviceId, name: c.label || `Camera ${i + 1}`, type: 'camera' }))
		.sort(byName)

	const videos = [...files.map(fileItem), ...missing.map(m => ({ ...m, type: 'video_file', state: 'missing' }))].sort(
		byName
	)

	const shaders = SHADER_NAMES.filter(name => SHOW_DEV_UI || isShipped(name))
		.map(name => ({
			id: `shader:${name}`,
			name: SHADERS[name].label || name,
			type: 'shader',
		}))
		.sort(byName)

	return [
		{ id: 'none', name: 'None', type: 'none' },
		...devices,
		{ id: 'screen', name: 'Screen Capture', type: 'screen' },
		{ id: 'video_file', name: 'Open file…', type: 'video_file' },
		...videos,
		...shaders,
	]
}

/** A pick → the source id a fill saves: any device is 'camera'. */
export const sourceKind = id => {
	if (!id || id === 'none') return 'none'
	if (isShaderSource(id) || isFileSource(id) || FIXED.includes(id)) return id
	return CAMERA_SOURCE
}

/** A fill source → its Sources item: 'camera' is this machine's device. */
export const sourceItemId = (source, device) => (source === CAMERA_SOURCE ? (device ?? 'none') : (source ?? 'none'))

/** Mirror rule: physical cameras flip, everything else (incl. label-recognized virtual cameras) doesn't. */
export const shouldFlipForSource = (value, cameras = []) =>
	value !== 'none' &&
	value !== 'screen' &&
	value !== 'video_file' &&
	!isFileSource(value) &&
	!isShaderSource(value) &&
	!isVirtualCamera(cameras.find(c => c.deviceId === value)?.label)

const FILL_TYPES = new Set(['rect', 'circle', 'path', 'brush'])

const canFill = obj => !!obj && FILL_TYPES.has(obj.type) && !obj.maskFor

/** The shape a pick fills and Source Settings follows: the last selected fill-able shape, else the last source-filled shape touched; null for none (a pick then adds a full-stage rect). */
export const selectFillTarget = createSelector(
	state => state.live.objects,
	state => state.selection.objectIds,
	state => state.editor.last_fill_id,
	(objects, selected, last) => {
		const picked = selected?.findLast(id => canFill(objects[id]))
		if (picked) return picked
		return last && canFill(objects[last]) && isSourceFill(objects[last].fill) ? last : null
	}
)

/** The target's fill source, or null when it has none. */
export const selectTargetSource = state => {
	const fill = state.live.objects[selectFillTarget(state)]?.fill
	return isSourceFill(fill) ? fill.source : null
}

/** The Sources item the target shows ('none' without a source fill). */
export const selectTargetItem = state => sourceItemId(selectTargetSource(state), state.camera.device)

/** The camera and screen pictures a set of presets' fills show, on this machine. */
export const presetStreamSources = (presets = [], device) => {
	const ids = new Set()
	for (const p of presets)
		for (const { id } of fillPictures(p?.settings?.objects, { device, bags: p?.settings?.source_settings }))
			if (id && !isFileSource(id)) ids.add(id)
	return [...ids]
}

// The fade a source-settings load opened, so a second load may interrupt it the way a preset load would.
let sourceFade = null

/** Fades `live` into `next` like a preset load, then `commit`s; commits at once when a fade cannot run. */
const fadeLive = (dispatch, next, commit, instant) =>
	dispatch((d, getState) => {
		const s = getState()
		const { fade_duration, instant: globalInstant } = s.playback
		const ours = transitionManager.isActive() && transitionManager.timeline === sourceFade
		const fading =
			!instant &&
			!globalInstant &&
			fade_duration &&
			selectSettings(s).use_pixi &&
			selectTexturesReady(s) &&
			!transitionManager.isRun() &&
			(ours || !transitionManager.isActive()) &&
			transitionManager.preloadTextures(next) === true
		if (!fading) return commit(d)
		// The destination's pictures open now, as a preset load's do.
		stageIncoming(next)
		const from = ours ? (transitionManager.getActiveState() ?? s.live) : s.live
		const tl = transitionManager.createTransition(from, next, fade_duration, selectLiveProps(s))
		sourceFade = tl
		tl.eventCallback('onComplete', () => {
			if (sourceFade === tl) sourceFade = null
			commit(d)
		})
	})

/** `source`'s bag becomes `values`, faded like a preset load (Alt: instant). */
export const setSourceSettings = (dispatch, source, values, { instant = false } = {}) =>
	dispatch((d, getState) => {
		const live = getState().live
		const next = { ...live, source_settings: { ...live.source_settings, [source]: values } }
		fadeLive(d, next, dd => dd(setSourceValues({ source, values })), instant)
	})

const fullStageRect = () => ({
	type: 'path',
	name: 'Video',
	width: WIDTH,
	height: HEIGHT,
	data: boxData(WIDTH, HEIGHT),
})

// Every object id under root, in draw order: the last is drawn on top.
const drawOrder = objects => {
	const out = []
	const walk = id => {
		for (const child of objects[id]?.children ?? []) {
			out.push(child)
			walk(child)
		}
	}
	walk('root')
	return out
}

/** The shapes a pick fills, `[]` for a new full-stage layer. `auto` and `replace`: the target Source Settings follows (`selectFillTarget`), else the topmost source layer. `add`: a new layer. */
const pickTargets = (state, mode) => {
	if (mode === 'add') return []
	const { objects } = state.live
	const target = selectFillTarget(state)
	if (target) return [target]
	const top = drawOrder(objects).findLast(id => canFill(objects[id]) && isSourceFill(objects[id].fill))
	return top ? [top] : []
}

/** Fills `source` into the fills on `from` when given, else `pickTargets`, `values` over its bag, faded like a preset load (`instant` cuts); a new full-stage rect takes it, selected, when that names none. A fill keeps its fit. */
const fillTarget = (dispatch, source, values, { from, mode, instant, shapes }) =>
	dispatch((d, getState) => {
		const { live } = getState()
		const { objects } = live
		const ids = shapes ?? (from ? fillUsersOf(objects, from) : pickTargets(getState(), mode))
		const fitOf = id => (isSourceFill(objects[id]?.fill) ? objects[id].fill.fit : undefined)
		const fill = source === 'none' ? '#00000000' : sourceFill(source, fitOf(ids[0]))
		if (ids.length) {
			const action = applySourceFill({ ids, fill, values })
			// Emptying a removed video's fills cuts: its picture is gone.
			return fadeLive(d, liveReducer(live, action), dd => dd(action), instant || (!!from && source === 'none'))
		}
		if (source === 'none' || from) return
		const id = newObjectId('path', objects)
		const action = applySourceFill({ ids: [id], object: fullStageRect(), fill, values })
		fadeLive(
			d,
			liveReducer(live, action),
			dd => {
				dd(action)
				dd(setObjectSelection([id]))
			},
			instant
		)
	})

/** Shapes `ids` show `source`, already on stage, faded like any pick. */
export const fillShapes = (dispatch, ids, source) => fillTarget(dispatch, source, undefined, { shapes: ids })

/** The one pick entry point: the shapes `pickTargets` names for `mode` (`auto` | `replace` | `add`) take the picked source, or with `from` every fill on that source. A device becomes this machine's camera; 'video_file' opens the file picker and a `file:<id>` reopens its library entry. `settings` is a shader preset's bag, written with the fill; `instant` cuts instead of fading. */
export const pickSource = async (dispatch, id, cameras, { settings, from, mode = 'auto', instant = false } = {}) => {
	// A pick is never a preset's cue: a file goes on stage at its own playhead.
	dropCues()
	if (id === 'video_file' || isFileSource(id)) {
		const result = id === 'video_file' ? await pickVideoFile() : await openLibraryFile(libraryIdOf(id))
		if (!result) return
		const { id: libraryId, file, fileName, duration } = result
		const source = fileSourceId(libraryId ?? `local-${crypto.randomUUID()}`)
		provide(source, { file, fileName })
		fillTarget(
			dispatch,
			source,
			{ file: fingerprintOf({ name: fileName, size: file.size, duration }) },
			{ from, mode, instant }
		)
		return
	}
	const source = sourceKind(id)
	if (source === CAMERA_SOURCE) {
		rememberCamera(id)
		dispatch(setCameraDevice(id))
	}
	if (source === 'screen') allowScreenPrompt()
	// A webcam is a self-view: picking one re-decides its mirror.
	const values = source === CAMERA_SOURCE ? { ...settings, mirror: shouldFlipForSource(id, cameras) } : settings
	fillTarget(dispatch, source, values, { from, mode, instant })
	// A capture opens inside the click that asked for it.
	syncFillStage()
}
